import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getLiveMissionsData } from "Slices/missionsPage/selectors";
import { getVehicleTypes } from "Slices/vehicleTypes";
import { getVehicles } from "Slices/vehicles";
import { getCurrentSemanticMap } from "Slices/semanticMaps";
import { getVehiclesUpdateData } from "Slices/vehiclesState/selectors";
import {
  filterMissionsDataBySearch,
  countMissionsByStatusData,
  isValidMissionData,
  sortMissionsByDeploymentDate,
} from "./liveMissionsUtils";

/**
 * Custom hook for managing live missions data
 * @param searchValue - Search term for filtering missions
 * @returns Object with processed missions data and statistics
 */
export const useLiveMissionsData = (searchValue: string) => {
  const vehicles = useSelector(getVehicles);
  const vehicleTypes = useSelector(getVehicleTypes);
  const liveMissions = useSelector(getLiveMissionsData);
  const currentSemanticMap = useSelector(getCurrentSemanticMap);
  const vehicleStates = useSelector(getVehiclesUpdateData);

  // Process and validate missions
  const processedMissions = useMemo(() => {
    if (!liveMissions || !Array.isArray(liveMissions)) {
      return [];
    }

    // Filter valid missions
    return liveMissions.filter(isValidMissionData);
  }, [liveMissions]);

  // Filter missions by search and sort by deployed at (descending)
  const filteredMissions = useMemo(() => {
    const filtered = filterMissionsDataBySearch(processedMissions, searchValue);
    return sortMissionsByDeploymentDate(filtered);
  }, [processedMissions, searchValue]);

  // Calculate statistics
  const missionStats = useMemo(() => {
    const statusCounts = countMissionsByStatusData(processedMissions);
    return {
      total: processedMissions.length,
      filtered: filteredMissions.length,
      byStatus: statusCounts,
    };
  }, [processedMissions, filteredMissions]);

  return {
    vehicles,
    vehicleTypes,
    liveMissions: filteredMissions,
    currentSemanticMap,
    missionStats,
    vehicleStates,
    isLoading: !liveMissions,
  };
};
